import {
  PaypalBillingAgreementResponse,
  PaypalTokenRequestParameters,
  PaypalTokenResponse,
  PaypalOrderResponse,
  PaypalBillingAgreementDetailsResponse,
  SimplifiedPaypalOrder,
} from 'models/Paypal'
import { api, usePrefetchNow } from '../api'

const paypalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // This isn't really a token, but naming convention from classypay is followed
    // it returns merchant account info
    getPaypalToken: builder.query<PaypalTokenResponse, PaypalTokenRequestParameters>({
      query: ({ campaignId, currency }) => ({
        url: `/token/paypal?campaignId=${campaignId}&currency=${currency}`,
      }),
    }),

    // Following classypay's naming convention. Unlike the above call this creates an object
    // (and makes a POST request to paypal despite being a GET request to pay)
    // it returns a billing agreement id for recurring transactions
    getPaypalBillingAgreement: builder.query<
      PaypalBillingAgreementResponse,
      PaypalTokenRequestParameters
    >({
      query: ({ campaignId, currency }) => ({
        url: `/token/paypalBillingAgreement?campaignId=${campaignId}&currency=${currency}`,
      }),
    }),

    // call classypay which will create an order using the paypal rest api
    createOrder: builder.query<
      { orderId: string },
      { campaignId: string; amount: string; currency: string; paymentMethod: string }
    >({
      query: ({ campaignId, amount, currency, paymentMethod }) => ({
        url: `/integrations/paypal/order?campaignId=${campaignId}`,
        method: 'POST',
        body: { amount, currency, paymentMethod },
      }),
    }),

    // directly calls paypal api for order info
    getOrder: builder.query<SimplifiedPaypalOrder, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `integrations/paypal/orders/${orderId}`,
      }),
      transformResponse: (response: PaypalOrderResponse) => {
        return {
          first_name: response.payer.name.given_name,
          last_name: response.payer.name.surname,
          email: response.payer.email_address,
          address_line_1: response.payer.address?.address_line_1,
          address_line_2: response.payer.address?.address_line_2,
          city: response.payer.address?.admin_area_2,
          state: response.payer.address?.admin_area_1,
          postal_code: response.payer.address?.postal_code,
          country_code: response.payer.address?.country_code,
          payment_source: response.payment_source.venmo ? 'venmo' : 'paypal',
        }
      },
    }),

    // directly calls paypal api for billing agreement details
    getBillingAgreementTokenDetails: builder.query<
      SimplifiedPaypalOrder,
      { billingAgreementTokenId: string }
    >({
      query: ({ billingAgreementTokenId }) => ({
        url: `integrations/paypal/billing-agreements/${billingAgreementTokenId}`,
      }),
      transformResponse: (response: PaypalBillingAgreementDetailsResponse) => {
        return {
          first_name: response.payer_info.first_name,
          last_name: response.payer_info.last_name,
          email: response.payer_info.email,
          address_line_1: response.shipping_address.line1,
          address_line_2: response.shipping_address.line2,
          city: response.shipping_address.city,
          state: response.shipping_address.state,
          postal_code: response.shipping_address.postal_code,
          country_code: response.shipping_address.country_code,
        }
      },
    }),
  }),
})

export const usePrefetchPaypalToken = (campaignId: string, currency: string) =>
  usePrefetchNow(
    paypalApi.endpoints.getPaypalToken,
    { campaignId, currency },
    !!campaignId && !!currency,
  )

export default paypalApi
